import { nanoid } from "@reduxjs/toolkit";
import { makeAutoObservable } from "mobx";

import { FormElementAttributes, FormElementKind } from "@/gql/graphql";

import Form from "./Form";
import FormElement from "./FormElement";
import { FormElementAttributesType } from "../components/utils/types";

export default class Section {
  id?: string;
  section: number;
  arguments: Record<string, any> = {};
  elements: FormElement[] = [];
  form: Form;

  /**
   * For react `key` prop only.
   */
  key = nanoid();

  get isFirst(): boolean {
    return this.section === 1;
  }

  get isLast(): boolean {
    return this.section === this.form.sections.length;
  }

  get isOnlySection(): boolean {
    return this.form.sections.length === 1;
  }

  get defaultButtonLabel(): string {
    return this.isLast ? "Submit" : "Next Section";
  }

  constructor(form: Form, section: number) {
    makeAutoObservable(this);
    this.form = form;
    this.section = section;
  }

  setArguments(args: Record<string, any>): void {
    this.arguments = args;
  }

  setArgument(argument: string, value: any): void {
    this.arguments[argument] = value;
  }

  mergeUp(): void {
    this.form.mergeUpSection(this);
  }

  remove(): void {
    this.form.removeSection(this);
  }

  addElementKind(type: FormElementAttributesType): FormElement {
    const element = new FormElement(this, type.kind);
    element.arguments = type.arguments;
    this.elements.push(element);
    return element;
  }

  addElement(element: FormElement): FormElement {
    element.section = this;
    this.elements.push(element);
    return element;
  }

  swapElements(elementA: FormElement, elementB: FormElement): void {
    // Swap their places in array.
    const indexOfA = this.elements.indexOf(elementA);
    const indexOfB = this.elements.indexOf(elementB);
    this.elements.splice(indexOfA, 1, elementB);
    this.elements.splice(indexOfB, 1, elementA);
  }

  removeElement(element: FormElement): void {
    this.elements.splice(this.elements.indexOf(element), 1);
  }

  toAttributes(): FormElementAttributes {
    return {
      id: this.id,
      kind: FormElementKind.Section,
      arguments: this.arguments,
    };
  }
}
